import { Injectable } from "@angular/core";
import { FileLogger } from "@comunicare/comunicare-helper";
import { catchError, map, Observable, of } from "rxjs";
import { ApiService } from "src/app/shared/services/api.service";
import {
  I18nTarget,
  IExposedInstanceLanguage,
  II18nTranslation,
  ILanguageCreationDetails,
  ILanguageModificationDetails,
} from "../interfaces/language.interface";

@Injectable({
  providedIn: "root",
})
export class LanguageService {
  constructor(private api: ApiService) {}

  /**
   * Retrieves the languages configured for the instance.
   * @return The list of exposed instance languages.
   */
  public getLanguages(): Observable<IExposedInstanceLanguage[]> {
    return this.api.get("instance/languages", undefined, false) as Observable<IExposedInstanceLanguage[]>;
  }

  /**
   * Creates a new instance language.
   * @param lang Details of the language to create.
   * @return The newly created language.
   */
  public createLanguage(lang: ILanguageCreationDetails): Observable<IExposedInstanceLanguage> {
    return this.api.post("instance/languages", lang) as Observable<IExposedInstanceLanguage>;
  }

  /**
   * Updates an existing instance language.
   * @param langId Identifier of the language to update.
   * @param lang Fields to update on the language.
   * @return Success or error.
   */
  public updateLanguage(langId: number, lang: ILanguageModificationDetails): Observable<unknown> {
    return this.api.patch(`instance/languages/${langId}`, lang) as Observable<unknown>;
  }

  /**
   * Disables an instance language.
   * @param langId Identifier of the language to delete.
   * @return Success or error.
   */
  public deleteLanguage(langId: number): Observable<unknown> {
    return this.api.patch(`instance/languages/${langId}/disable`, undefined) as Observable<unknown>;
  }

  /**
   * Retrieves the i18n translations of the dashboard for the given languages.
   * @param languages Comma-separated language codes.
   * @return The translation map, or an empty object if the server fails or sends back no translations.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public getTranslation(languages: string): Observable<any> {
    return (
      this.api.get(
        "instance/i18nTranslation",
        {
          target: I18nTarget.EPICARE_DASHBOARD,
          languages,
        },
        false
      ) as Observable<II18nTranslation[]>
    ).pipe(
      catchError((error) => {
        FileLogger.error("LanguageService", "Server error while downloading translations", error);
        return of(null);
      }),
      map((serverTranslations) => {
        if (!serverTranslations || serverTranslations.length < 1) {
          FileLogger.error("LanguageService", "Server sent back empty translations");
          return {};
        }
        return serverTranslations[0].i18n;
      })
    );
  }
}
